import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import css from './pricing.module.css';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { getCSRFToken } from '../../lib/csrf';
import { cancelSubscription } from '../../lib/cancelSubscriptionApi';

const PayWall = ({ authUser, plans, currentSubscription, isPollingComplete }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Handle sign in redirect
  const handleSignIn = (e) => {
    e.preventDefault();
    navigate('/signin');
  };

  // Handle plan button click with ripple and optional payment
  const handleButtonClick = async (e, productId) => {
    const button = e.currentTarget;
    const rect = button.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Ripple effect
    const ripple = document.createElement('span');
    ripple.style.cssText = `
      position: absolute;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.5);
      width: 0;
      height: 0;
      top: ${y}px;
      left: ${x}px;
      transform: translate(-50%, -50%);
      animation: ripple-effect 0.6s ease-out;
    `;

    button.appendChild(ripple);
    setTimeout(() => ripple.remove(), 600);

    // Log plan selection
    const card = button.closest(`.${css.pricingCard}`);
    const planName = card.querySelector(`.${css.planName}`).textContent;
    console.log(`${planName} plan selected`);
    console.log('Product ID:', productId);
    
    if (productId) {
      try {
        const csrfToken = getCSRFToken();
        console.log('Creating subscription with product_id:', productId);
        
        const response = await axios.post('/api/billing/subscription/create/', {
          product_id: parseInt(productId)
        }, {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
            ...(csrfToken && { 'X-CSRFToken': csrfToken })
          }
        });
        
        console.log('Subscription created:', response.data);
        
        if (response.data.page_url) {
          console.log('Redirecting to:', response.data.page_url);
          window.location.href = response.data.page_url;
        } else {
          console.error('No page_url in response');
        }
      } catch (error) {
        console.error('Error creating subscription:', error);
      }
    }
  };

  useEffect(() => {
    // Parallax background circles only
    const handleMouseMove = (e) => {
      const circles = document.querySelectorAll(`.${css.bgCircle}`);
      const x = e.clientX / window.innerWidth;
      const y = e.clientY / window.innerHeight;

      circles.forEach((circle, index) => {
        const speed = (index + 1) * 10;
        circle.style.transform = `translate(${x * speed}px, ${y * speed}px)`;
      });
    };

    document.addEventListener('mousemove', handleMouseMove);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  return (
    <div className={css.pricingContainer}>
      {/* 
      <div className={css.bgCircle}></div>
      <div className={css.bgCircle}></div>
      <div className={css.bgCircle}></div>*/}

      <div className={css.container}>
        <div className={css.header}>
          <h1>{t("Plans that fit your needs")}</h1>
          <p>{t("Simple, scalable pricing that grows with you.")}</p>
        </div>

        <div className={css.pricingGrid}>
          {/* Free */}
          <div className={css.pricingCard}>
            <h3 className={css.planName}>{t("Free")}</h3>
            <div className={css.planPrice}>
              <sup>₴</sup>0
            </div>
            <p className={css.planDesc}>{t("Free for everyone")}</p>
            <div style={{ textAlign: 'center' }}>
              <span style={{ display: 'inline-block', height: '29px' }}></span>
            </div>
            <ul className={css.features}>
              <li>{t("Watch learning videos")}</li>
              <li>{t("Add words to personal vocabulary")}</li>
              <li>{t("Study & review words")}</li>
              <li className={css.notIncluded}>{t("Video comprehension checks")}</li>
              <li className={css.notIncluded}>{t("AI voice dialogues")}</li>
              <li className={css.notIncluded}>{t("Certificate upon completion")}</li>
              <li className={css.notIncluded}>{t("Priority support")}</li>
            </ul>
            <button style={{opacity: 0, pointerEvents: 'none'}} 
              className={`${css.planButton} ${css.btnSecondary}`}
            >
              {t("Sign Up")}
            </button>
          </div>

          {/* Dynamic Plans from Backend */}
          {plans && plans.map((plan, index) => {
            const isStandard = plan.name.includes('Стандарт');
            const isPremium = plan.name.includes('Преміум');
            const isFeatured = isStandard; // Standard plan is featured
            const isCurrentPlan = currentSubscription && currentSubscription.product.id === plan.id;
            const isActive = currentSubscription && Number(currentSubscription.status) === 2;
            const showActiveLabel = isCurrentPlan && isActive;
            
            return (
              <div key={plan.id} className={`${css.pricingCard} ${showActiveLabel ? css.featured : ''}`}>
                {showActiveLabel ? (
                  <div className={`${css.badge} ${css.badgeActive}`}>
                    {currentSubscription.status_display}
                  </div>
                ) : isFeatured && (!isCurrentPlan || !isActive) ? (
                  <div className={css.badge}>{t("Popular")}</div>
                  
                ) : null}
                <h3 className={css.planName}>{t(`${plan.name}`)}</h3>
                <div className={css.planPrice}>
                  <sup>₴</sup>{parseFloat(plan.price).toFixed(0)}<span>{plan.duration_days === 1 ? t("/pack") : t("/month")}</span>
                </div>
                <p className={css.planDesc}>{t(`${plan.description}`) || t("Awesome plan for you")}</p>
                <div style={{ textAlign: 'center' }}>
                  <span style={{ display: 'inline-block', height: '29px' }}></span>
                </div>
                <ul className={css.features}>
                  <li>{t("Watch learning videos")}</li>
                  <li>{t("Add words to personal vocabulary")}</li>
                  <li>{t("Study & review words")}</li>
                  <li>{t("Video comprehension checks")}</li>
                  {isStandard && <li className={css.highlight}>{t("AI voice dialogues — 10 per day")}</li>}
                  {isPremium && <li className={css.highlight}>{t("Unlimited AI voice dialogues")}</li>}
                  <li>{t("Certificate upon completion")}</li>
                  {!isStandard && <li>{t("Priority support")}</li>}
                </ul>
                <button 
                  className={`${css.planButton} ${showActiveLabel ? css.btnPrimary : css.btnSecondary}`}
                  onClick={showActiveLabel ? (e) => {
                    e.preventDefault();
                    cancelSubscription();
                  } : authUser?.isAuthenticated ? (e) => handleButtonClick(e, plan.id) : handleSignIn}
                >
                  {showActiveLabel ? t("Cancel") : (currentSubscription && isActive && !isCurrentPlan) ? t("Upgrade") : authUser?.isAuthenticated ? t("Get Started") : t("signin")}
                </button>
              </div>
            );
          })}

          {/* B2B/Индивидуальная Цена */}
          <div className={`${css.pricingCard} ${css.customPlan}`}>
            <h3 className={css.planName}>B2B</h3>
            <div className={`${css.planPrice} ${css.customPrice}`}>
              {t("Custom Pricing")}
            </div>
            <p className={css.planDesc}>{t("For schools, businesses, universities")}</p>
            <div style={{ textAlign: 'center' }}>
              <span style={{ display: 'inline-block', height: '29px' }}></span>
            </div>
            <ul className={css.features}>
              <li>{t("All Pro features included")}</li>
              <li>{t("Per-user progress reports")}</li>
              <li>{t("Training reminders for learners")}</li>
              <li>{t("Custom branding options")}</li>
              <li>{t("Dedicated account manager")}</li>
              <li>{t("Volume discounts available")}</li>
              <li>{t("API access & integrations")}</li>
            </ul>
            <button 
              className={`${css.planButton} ${css.btnSecondary}`}
              onClick={(e) => handleButtonClick(e, null)}
            >
              {t("Contact Us")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayWall;